import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../context/API';
import moment from 'moment';

export interface Period {
  startDate: Date | string;
  endDate: Date | string;
  label: string;
}

export interface PeriodParam {
  startDate: string;
  endDate: string;
  label: string;
}

export interface PerformanceComparisonRequest {
  itemId: string;
  periods: PeriodParam[];
  merchantId: string;
  store: string | null;
}

export interface ComparisonData {
  label: string;
  startDate: string;
  endDate: string;
  totalTimesSold: number;
  totalRevenue: number;
  volumeGrowth: number;
  revenueGrowth: number;
  trend: 'up' | 'down' | 'neutral';
  itemFound: boolean;
}

export interface PerformanceSummary {
  totalRevenue: number;
  totalVolume: number;
  averageRevenue: number;
  averageVolume: number;
}

export interface PerformanceData {
  itemId: string;
  summary: PerformanceSummary;
  comparisons: ComparisonData[];
}

interface ItemPerformanceComparisonState {
  performanceData: PerformanceData | null;
  loading: boolean;
  error: string | null;
}

const initialState: ItemPerformanceComparisonState = {
  performanceData: null,
  loading: false,
  error: null,
};

export const fetchPerformanceComparison = createAsyncThunk<
  PerformanceData,
  PerformanceComparisonRequest,
  { rejectValue: string }
>(
  'itemPerformanceComparison/fetchPerformanceComparison',
  async (params, { rejectWithValue }) => {
    try {
      const periodsParam = params.periods.map((period) => ({
        startDate: moment(period.startDate).format('YYYY-MM-DD'),
        endDate: moment(period.endDate).format('YYYY-MM-DD'),
        label: period.label,
      }));

      const requestParams = {
        periods: JSON.stringify(periodsParam),
        merchantId: params.merchantId,
        store: params.store,
      };

      const token = localStorage.getItem('access_token');
      const response = await API.get(
        `/analytics/items/${params.itemId}/performance-comparison`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: requestParams,
        }
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        return rejectWithValue('Failed to fetch performance comparison');
      }
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch performance comparison'
      );
    }
  }
);

const itemPerformanceComparisonSlice = createSlice({
  name: 'itemPerformanceComparison',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPerformanceComparison.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPerformanceComparison.fulfilled, (state, action) => {
        state.loading = false;
        state.performanceData = action.payload;
      })
      .addCase(fetchPerformanceComparison.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = itemPerformanceComparisonSlice.actions;
export default itemPerformanceComparisonSlice.reducer;
